// Desktop floating bug-report pill. Truly draggable per dev-sync note
// 3b7f7c31 / docs/planning/bug_report_placement_v2.md. The pill persists
// its position to localStorage (viewport-scaled so a different display
// reproject sanely), and the expanded panel sits ABOVE the pill anchored
// at the pill's resting position.
//
// On mobile breakpoints (<= 767px) this component is intentionally NOT
// mounted — the entry lives in the sidebar drawer as
// `BugReportMobileEntry` to free the bottom-right region for the
// `MobileBottomTabBar`. See `LeadWorkspaceShellModals.tsx` for the mount
// gate.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { useBugReportPanel } from "@/components/common/useBugReportPanel";
import { useBugReportPanelOpenBroadcast } from "@/components/common/useBugReportPanelOpenBroadcast";
import { useBugReportDockPosition } from "@/components/common/useBugReportDockPosition";
import { computePanelPlacement } from "@/components/common/bugReportPanelPlacement";
import type { BugReportDevModeSessionInfo } from "@/components/common/BugReportDevModeFooter";
import { BugReportPanelContent } from "@/components/common/BugReportPanelContent";

export type { BugReportDevModeSessionInfo };

export type BugReportButtonProps = {
  mode: PrimaryMode;
  devMode?: boolean;
  requestDevMode?: () => void;
  disableDevMode?: () => void;
  getSessionInfo?: () => BugReportDevModeSessionInfo;
};

/** Pixels the pointer must move before a tap upgrades to a drag. */
const DRAG_THRESHOLD_PX = 6;

/** Default pill measurements used before the element has been measured. */
const FALLBACK_PILL_W = 156;
const FALLBACK_PILL_H = 32;

export function BugReportButton({
  mode,
  devMode,
  requestDevMode,
  disableDevMode,
  getSessionInfo,
}: BugReportButtonProps) {
  const [open, setOpen] = useState(false);
  const panel = useBugReportPanel({ mode, loadOnOpenKey: open });
  // Broadcasts open/close transitions for the activity dock's
  // auto-collapse subscriber. See docs/planning/bug_report_placement_v2.md.
  useBugReportPanelOpenBroadcast(open, "desktop");

  // Track the pill's actual rendered size so we clamp drag releases tightly.
  // A `useLayoutEffect` + ResizeObserver feels heavier than necessary;
  // measure once on mount and on every render where the badge appears /
  // disappears (which changes the width).
  const pillRef = useRef<HTMLButtonElement | null>(null);
  const [pillSize, setPillSize] = useState({ w: FALLBACK_PILL_W, h: FALLBACK_PILL_H });
  useLayoutEffect(() => {
    if (!pillRef.current) return;
    const rect = pillRef.current.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setPillSize((prev) =>
        prev.w === rect.width && prev.h === rect.height ? prev : { w: rect.width, h: rect.height },
      );
    }
  }, [panel.openCount, open]);

  const dock = useBugReportDockPosition({ elementW: pillSize.w, elementH: pillSize.h });

  // Tap-vs-drag distinction copied from ActivityFloatingDock — clicking the
  // pill toggles open/closed; pressing+dragging moves it.
  const downRef = useRef<{ x: number; y: number; moved: boolean } | null>(null);

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      downRef.current = { x: e.clientX, y: e.clientY, moved: false };
    },
    [],
  );

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      const down = downRef.current;
      if (!down) return;
      const dx = Math.abs(e.clientX - down.x);
      const dy = Math.abs(e.clientY - down.y);
      if (!down.moved) {
        if (dx < DRAG_THRESHOLD_PX && dy < DRAG_THRESHOLD_PX) return;
        down.moved = true;
        if (open) setOpen(false);
        dock.beginDrag(e.clientX, e.clientY);
      } else {
        dock.updateDrag(e.clientX, e.clientY);
      }
    },
    [dock, open],
  );

  const handlePointerUp = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      const target = e.currentTarget;
      if (target.hasPointerCapture(e.pointerId)) target.releasePointerCapture(e.pointerId);
      const down = downRef.current;
      downRef.current = null;
      if (!down) return;
      if (down.moved) {
        dock.endDrag(e.clientX, e.clientY);
      } else {
        setOpen((prev) => !prev);
      }
    },
    [dock],
  );

  const handlePointerCancel = useCallback(() => {
    downRef.current = null;
    dock.cancelDrag();
  }, [dock]);

  // Close on outside click — only when the panel is actually open AND we are
  // not mid-drag. Without the drag guard, releasing a drag outside the
  // (also-closing) panel could fire two open-flips in a row.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  if (!panel.isAuth) return null;

  const sessionInfo = getSessionInfo ? getSessionInfo() : null;
  const showDevModeFooter =
    panel.canQueueSync && !!requestDevMode && !!disableDevMode;

  // Resting at the saved position, mid-drag at the cursor's offset position.
  const renderedPos = dock.dragPreview ?? dock.position;
  const placement = computePanelPlacement({
    pillX: renderedPos.x,
    pillY: renderedPos.y,
    pillW: pillSize.w,
    pillH: pillSize.h,
    viewportW: typeof window !== "undefined" ? window.innerWidth : 1280,
    viewportH: typeof window !== "undefined" ? window.innerHeight : 800,
    panelMaxHeight: 480,
    panelWidth: 320,
    offsetPx: 8,
    viewportMarginPx: 8,
  });

  return (
    <div
      ref={wrapperRef}
      data-bug-report-panel="desktop"
      className="z-50"
      style={{ position: "fixed", left: 0, top: 0, pointerEvents: "none" }}
    >
      {open && !dock.isDragging ? (
        <div
          className="pointer-events-auto w-80 rounded-2xl border border-gray-200 bg-white shadow-xl shadow-black/10 overflow-hidden max-h-[80vh] flex flex-col"
          style={{
            position: "fixed",
            left: placement.left,
            top: placement.top,
          }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50 shrink-0">
            <span className="text-sm font-semibold text-gray-900">Report a Bug</span>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-700 text-lg leading-none"
              onClick={() => setOpen(false)}
              aria-label="Close report a bug"
            >
              ×
            </button>
          </div>
          <div className="overflow-y-auto">
            <BugReportPanelContent
              mode={mode}
              description={panel.description}
              onDescriptionChange={panel.setDescription}
              onDictationAppend={panel.appendDictation}
              submitting={panel.submitting}
              submitError={panel.submitError}
              submitDone={panel.submitDone}
              submitDoneDevSync={panel.submitDoneDevSync}
              canQueueSync={panel.canQueueSync}
              isOwnerSync={panel.isOwnerSync}
              queueForDevSync={panel.queueForDevSync}
              setQueueForDevSync={panel.setQueueForDevSync}
              repoPath={panel.repoPath}
              setRepoPath={panel.setRepoPath}
              setGraphicFile={panel.setGraphicFile}
              onSubmit={() => void panel.submitReport()}
              myReports={panel.myReports}
              reportsLoading={panel.reportsLoading}
              openCount={panel.openCount}
              devModeFooter={
                showDevModeFooter
                  ? {
                      devMode: !!devMode,
                      sessionInfo,
                      onEnable: requestDevMode!,
                      onDisable: disableDevMode!,
                    }
                  : null
              }
            />
          </div>
        </div>
      ) : null}

      {/*
       * The pill is hidden while the panel is open so it can't paint on
       * top of the panel content (per dev-sync feedback 2026-05-28). It
       * stays mounted in the DOM so `pillRef.current.getBoundingClientRect()`
       * still works for measurement; we just remove it from the visual
       * + interactive layer via opacity + pointer-events-none.
       *
       * Mid-drag the panel is auto-closed (see handlePointerMove), so the
       * pill is always visible during a drag.
       */}
      <button
        ref={pillRef}
        type="button"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onContextMenu={(e) => {
          // Right-click on the pill = reset to default position. No
          // dedicated dev menu / context menu surface today
          // (per dev-sync note 3b7f7c31), so use the most natural place.
          e.preventDefault();
          dock.resetPosition();
        }}
        className={
          "touch-none select-none flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium shadow-sm transition cursor-grab active:cursor-grabbing " +
          (open
            ? "opacity-0 pointer-events-none border-rose-300 bg-rose-50 text-rose-900"
            : "pointer-events-auto border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:text-gray-700")
        }
        style={{
          position: "fixed",
          left: renderedPos.x,
          top: renderedPos.y,
        }}
        title="Drag to reposition · right-click to reset"
        aria-label="Report a bug"
        // Use `inert` (React 19) instead of `aria-hidden` so the focused-pill
        // case (user just clicked → panel opens → pill still has focus) doesn't
        // trigger Chrome's "aria-hidden on focused element" warning. `inert`
        // also prevents focus, so we can drop the `tabIndex={-1}` toggle.
        inert={open}
      >
        <span aria-hidden>🐛</span>
        <span>Report a bug</span>
        {panel.openCount > 0 && !open ? (
          <span className="ml-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold px-1.5 py-0 leading-5">
            {panel.openCount}
          </span>
        ) : null}
      </button>
    </div>
  );
}
